/* src/net/gameApi.ts — the game-state read and the cart mutation (TASKS.md 2.3, 2.9).
 *
 * ── Why RPC and not `GET /api/game/{playerId}` ────────────────────────────────────────
 * PLAN.md Part 3 §7 routes these through Supabase Edge Functions. Deploying one needs the
 * Supabase CLI (Docker/Homebrew), which this machine does not have — so the entry points are
 * `security definer` Postgres functions called over PostgREST instead (migration 0005 explains
 * the trade in full). The security property is unchanged and in one respect stronger: there is
 * no player id in the path or the body, because there is no parameter for one. The server
 * takes identity from the JWT's `sub` claim via `auth.uid()`, so the original's forgeable
 * `/api/game/{anyUUID}` has no equivalent here.
 *
 * ── Money crosses here, exactly once ──────────────────────────────────────────────────
 * Every money-shaped field arrives as a **string** (PLAN.md Part 3 §7) and is parsed into the
 * branded `Money` type in this file and nowhere else. Past this boundary nothing touches an
 * unparsed money string, and nothing downstream can accidentally treat a plain number as
 * currency (CLAUDE.md non-negotiable #2).
 */

import { getSupabase } from './supabaseClient.ts';
import {
  parseMoney,
  type AppealBreakdown,
  type Cart,
  type CatalogItem,
  type CuisineChoice,
  type Fixture,
  type GameState,
  type Holding,
  type LandState,
  type Parcel,
  type InventoryEntry,
  type MarketEntry,
  type Money,
  type RecipeLine,
  type ShopRate,
} from '../core/types.ts';

export class NotConfiguredError extends Error {
  constructor() {
    super('Supabase is not configured; set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
    this.name = 'NotConfiguredError';
  }
}

/** Narrow an unknown JSON value to a record without asserting its shape. */
function asRecord(v: unknown, what: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    throw new Error(`gameApi: expected an object for ${what}`);
  }
  return v as Record<string, unknown>;
}

function money(v: unknown, what: string): Money {
  if (typeof v !== 'string' && typeof v !== 'number') {
    throw new Error(`gameApi: ${what} is not a money value`);
  }
  return parseMoney(v);
}

function num(v: unknown, what: string): number {
  const n = typeof v === 'string' ? Number(v) : v;
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    throw new Error(`gameApi: ${what} is not a number`);
  }
  return n;
}

function str(v: unknown, what: string): string {
  if (typeof v !== 'string') throw new Error(`gameApi: ${what} is not a string`);
  return v;
}

function parseAppeal(raw: unknown): AppealBreakdown {
  const a = asRecord(raw, 'appealBreakdown');
  return {
    design: num(a['design'], 'appeal.design'),
    cleanliness: num(a['cleanliness'], 'appeal.cleanliness'),
    menu: num(a['menu'], 'appeal.menu'),
    tuneup: num(a['tuneup'], 'appeal.tuneup'),
    total: num(a['total'], 'appeal.total'),
  };
}

function parseCart(raw: unknown): Cart {
  const c = asRecord(raw, 'cart');
  // Before placement there is no pitch and no cuisine, so these are legitimately null.
  const placed = c['placed'] === true;
  return {
    id: str(c['id'], 'cart.id'),
    placed,
    name: str(c['name'], 'cart.name'),
    cuisine: typeof c['cuisine'] === 'string' ? c['cuisine'] : null,
    lon: typeof c['lon'] === 'number' ? c['lon'] : null,
    lat: typeof c['lat'] === 'number' ? c['lat'] : null,
    pricePerDog: money(c['pricePerDog'], 'cart.pricePerDog'),
    effectiveDogPrice: money(c['effectiveDogPrice'], 'cart.effectiveDogPrice'),
    unitCost: money(c['unitCost'], 'cart.unitCost'),
    optimalPrice: money(c['optimalPrice'], 'cart.optimalPrice'),
    readyServings: num(c['readyServings'], 'cart.readyServings'),
    preppedDogs: num(c['preppedDogs'], 'cart.preppedDogs'),
    servingsAvailable: num(c['servingsAvailable'], 'cart.servingsAvailable'),
    capacity: num(c['capacity'], 'cart.capacity'),
    prepCap: num(c['prepCap'], 'cart.prepCap'),
    cleanliness: num(c['cleanliness'], 'cart.cleanliness'),
    cookMastery: num(c['cookMastery'], 'cart.cookMastery'),
    soldToday: num(c['soldToday'], 'cart.soldToday'),
    customersPerMin: num(c['customersPerMin'], 'cart.customersPerMin'),
    appeal: num(c['appeal'], 'cart.appeal'),
    appealBreakdown: parseAppeal(c['appealBreakdown']),
    // Quantities, not money — the server sends these as JSON numbers.
    inventory: (c['inventory'] ?? {}) as Record<string, number>,
    recipe: Array.isArray(c['recipe'])
      ? c['recipe'].map((r): RecipeLine => {
          const e = asRecord(r, 'cart.recipe entry');
          return {
            itemId: str(e['itemId'], 'recipe.itemId'),
            name: str(e['name'], 'recipe.name'),
            qty: num(e['qty'], 'recipe.qty'),
            have: num(e['have'], 'recipe.have'),
          };
        })
      : [],
  };
}

function parseCuisine(raw: unknown): CuisineChoice {
  const c = asRecord(raw, 'cuisine');
  return {
    cuisine: str(c['cuisine'], 'cuisine.cuisine'),
    name: str(c['name'], 'cuisine.name').replace(/ Bundle$/, ''),
    bundle: str(c['bundle'], 'cuisine.bundle'),
    basePrice: money(c['basePrice'], 'cuisine.basePrice'),
    unitCost: money(c['unitCost'], 'cuisine.unitCost'),
    recipe: Array.isArray(c['recipe'])
      ? c['recipe'].map((r) => {
          const e = asRecord(r, 'cuisine.recipe entry');
          return { itemId: str(e['itemId'], 'recipe.itemId'), qty: num(e['qty'], 'recipe.qty') };
        })
      : [],
  };
}

function parseMarketEntry(raw: unknown): MarketEntry {
  const m = asRecord(raw, 'market entry');
  return {
    key: str(m['key'], 'market.key'),
    name: str(m['name'], 'market.name'),
    category: str(m['category'], 'market.category'),
    price: money(m['price'], 'market.price'),
    trendPct: num(m['trendPct'], 'market.trendPct'),
    spread: num(m['spread'], 'market.spread'),
    last: money(m['last'], 'market.last'),
    volume: num(m['volume'], 'market.volume'),
  };
}

function parseFixture(raw: unknown): Fixture {
  const f = asRecord(raw, 'fixture');
  const kind = str(f['kind'], 'fixture.kind');
  if (kind !== 'shelf' && kind !== 'checkout' && kind !== 'storage' && kind !== 'stairs') {
    throw new Error(`fixture.kind: unknown "${kind}"`);
  }
  return {
    id: str(f['id'], 'fixture.id'),
    kind,
    floor: num(f['floor'], 'fixture.floor'),
    slot: num(f['slot'], 'fixture.slot'),
    itemId: typeof f['itemId'] === 'string' ? f['itemId'] : null,
    qty: typeof f['qty'] === 'number' ? f['qty'] : 0,
    price: typeof f['price'] === 'string' ? money(f['price'], 'fixture.price') : null,
  };
}

function parseShopRate(raw: unknown): ShopRate | null {
  if (raw === null || raw === undefined) return null;
  const s = asRecord(raw, 'shop');
  return {
    open: s['open'] === true,
    customersPerMin: num(s['customersPerMin'], 'shop.customersPerMin'),
    revenuePerMin: money(s['revenuePerMin'], 'shop.revenuePerMin'),
    shelves: Array.isArray(s['shelves'])
      ? s['shelves'].map((sh) => {
          const r = asRecord(sh, 'shop.shelf');
          return {
            id: str(r['id'], 'shelf.id'),
            item: str(r['item'], 'shelf.item'),
            price: money(r['price'], 'shelf.price'),
            perMin: num(r['perMin'], 'shelf.perMin'),
          };
        })
      : [],
  };
}

export function parseParcel(raw: unknown): Parcel | null {
  if (raw === null || raw === undefined) return null;
  const p = asRecord(raw, 'parcel');
  const storageRaw = p['storage'];
  return {
    id: str(p['id'], 'parcel.id'),
    osmWayId: typeof p['osmWayId'] === 'number' ? p['osmWayId'] : null,
    landUse: typeof p['landUse'] === 'string' ? p['landUse'] : null,
    areaM2: num(p['areaM2'], 'parcel.areaM2'),
    levels: num(p['levels'], 'parcel.levels'),
    floors: typeof p['floors'] === 'number' ? p['floors'] : 1,
    roadClass: typeof p['roadClass'] === 'string' ? p['roadClass'] : null,
    price: money(p['price'], 'parcel.price'),
    lon: num(p['lon'], 'parcel.lon'),
    lat: num(p['lat'], 'parcel.lat'),
    ring: Array.isArray(p['ring'])
      ? p['ring'].flatMap((pt): Array<readonly [number, number]> => {
          if (!Array.isArray(pt) || pt.length < 2) return [];
          const lon = pt[0];
          const lat = pt[1];
          return typeof lon === 'number' && typeof lat === 'number' ? [[lon, lat] as const] : [];
        })
      : [],
    frontageDeg: typeof p['frontageDeg'] === 'number' ? p['frontageDeg'] : null,
    owned: p['owned'] === true,
    ownedByMe: p['ownedByMe'] === true,
    pricePaid: typeof p['pricePaid'] === 'string' ? money(p['pricePaid'], 'parcel.pricePaid') : null,
    resaleValue: typeof p['resaleValue'] === 'string' ? money(p['resaleValue'], 'parcel.resaleValue') : null,
    reachableFloors: typeof p['reachableFloors'] === 'number' ? p['reachableFloors'] : 1,
    floorSlots: typeof p['floorSlots'] === 'number' ? p['floorSlots'] : 4,
    stairsCost: typeof p['stairsCost'] === 'string' ? money(p['stairsCost'], 'parcel.stairsCost') : money(0, 'parcel.stairsCost'),
    fixtures: Array.isArray(p['fixtures']) ? p['fixtures'].map(parseFixture) : [],
    storage:
      storageRaw === null || storageRaw === undefined
        ? null
        : (() => {
            const s = asRecord(storageRaw, 'parcel.storage');
            const items = asRecord(s['items'] ?? {}, 'storage.items');
            const out: Record<string, number> = {};
            for (const [k, v] of Object.entries(items)) {
              if (typeof v === 'number') out[k] = v;
            }
            return {
              capacity: num(s['capacity'], 'storage.capacity'),
              used: num(s['used'], 'storage.used'),
              items: out,
            };
          })(),
    deliveries: Array.isArray(p['deliveries'])
      ? p['deliveries'].map((d) => {
          const r = asRecord(d, 'delivery');
          return {
            itemId: str(r['itemId'], 'delivery.itemId'),
            qty: num(r['qty'], 'delivery.qty'),
            arrivesAt: str(r['arrivesAt'], 'delivery.arrivesAt'),
          };
        })
      : [],
    shop: parseShopRate(p['shop']),
  };
}

function parseLand(raw: unknown): LandState {
  // Older servers predate the land block; an absent one is "you own nothing", not a parse error.
  if (raw === null || raw === undefined) {
    return { owned: 0, built: 0, portfolioValue: money(0, 'land'), rentPerMin: money(0, 'land'), holdings: [] };
  }
  const l = asRecord(raw, 'land');
  return {
    owned: num(l['owned'], 'land.owned'),
    built: num(l['built'], 'land.built'),
    portfolioValue: money(l['portfolioValue'], 'land.portfolioValue'),
    rentPerMin: money(l['rentPerMin'], 'land.rentPerMin'),
    holdings: Array.isArray(l['holdings'])
      ? l['holdings'].map((h): Holding => {
          const e = asRecord(h, 'holding');
          return {
            parcelId: str(e['parcelId'], 'holding.parcelId'),
            osmWayId: typeof e['osmWayId'] === 'number' ? e['osmWayId'] : null,
            areaM2: num(e['areaM2'], 'holding.areaM2'),
            price: money(e['price'], 'holding.price'),
            lon: num(e['lon'], 'holding.lon'),
            lat: num(e['lat'], 'holding.lat'),
            floors: typeof e['floors'] === 'number' ? e['floors'] : 1,
            fixtures: typeof e['fixtures'] === 'number' ? e['fixtures'] : 0,
            shopOpen: e['shopOpen'] === true,
            shopPerMin:
              typeof e['shopPerMin'] === 'string'
                ? money(e['shopPerMin'], 'holding.shopPerMin')
                : money(0, 'holding.shopPerMin'),
          };
        })
      : [],
  };
}

export function parseGameState(raw: unknown): GameState {
  const g = asRecord(raw, 'game state');
  const inc = asRecord(g['income'], 'income');
  const carts = Array.isArray(g['carts']) ? g['carts'].map(parseCart) : [];

  return {
    playerId: str(g['playerId'], 'playerId'),
    serverTime: str(g['serverTime'], 'serverTime'),
    cash: money(g['cash'], 'cash'),
    bank: money(g['bank'], 'bank'),
    netWorth: money(g['netWorth'], 'netWorth'),
    loyalty: num(g['loyalty'], 'loyalty'),
    day: num(g['day'], 'day'),
    run: num(g['run'], 'run'),
    income: {
      grossPerMin: money(inc['grossPerMin'], 'income.grossPerMin'),
      ingredientCostPerMin: money(inc['ingredientCostPerMin'], 'income.ingredientCostPerMin'),
      wagePerMin: money(inc['wagePerMin'], 'income.wagePerMin'),
      rentPerMin: money(inc['rentPerMin'], 'income.rentPerMin'),
      rentIncomePerMin: money(inc['rentIncomePerMin'], 'income.rentIncomePerMin'),
      netPerMin: money(inc['netPerMin'], 'income.netPerMin'),
      // Older servers do not send it; fall back to the profit rate rather than throwing.
      cashPerMin: money(inc['cashPerMin'] ?? inc['netPerMin'], 'income.cashPerMin'),
      interestPerMin: money(inc['interestPerMin'], 'income.interestPerMin'),
      dayRevenue: money(inc['dayRevenue'], 'income.dayRevenue'),
    },
    cart: g['cart'] === null || g['cart'] === undefined ? null : parseCart(g['cart']),
    carts,
    inventory: Array.isArray(g['inventory'])
      ? g['inventory'].map((r): InventoryEntry => {
          const e = asRecord(r, 'inventory entry');
          return { itemId: str(e['itemId'], 'inventory.itemId'), qty: num(e['qty'], 'inventory.qty') };
        })
      : [],
    market: Array.isArray(g['market']) ? g['market'].map(parseMarketEntry) : [],
    cuisines: Array.isArray(g['cuisines']) ? g['cuisines'].map(parseCuisine) : [],
    land: parseLand(g['land']),
  };
}

/**
 * Sign in anonymously if there is no session yet, and return the player id. Identity is the
 * JWT's `sub` claim — never a client-minted UUID in localStorage (PLAN.md Part 3 §8). The
 * server's `on_auth_user_created` trigger creates the player row, state and starter cart, so
 * the client never chooses its own starting position or balance.
 */
export async function ensureSignedIn(): Promise<string> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();

  const existing = await supabase.auth.getSession();
  const current = existing.data.session?.user.id;
  if (current !== undefined) return current;

  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) throw error;
  const id = data.user?.id;
  if (id === undefined) throw new Error('ensureSignedIn: no user on the new session');
  return id;
}

export async function fetchGameState(): Promise<GameState> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();

  const { data, error } = await supabase.rpc('game_state');
  if (error) throw new Error(`game_state: ${error.message}`);
  return parseGameState(data);
}

/**
 * Set a cart's price. An intent, not a result: the server re-runs the tick, applies the price,
 * and returns the whole freshly computed state — which the caller writes straight into the
 * store, so the UI reflects the change without waiting for the next poll.
 */
export async function setCartPrice(cartId: string, pricePerDog: number): Promise<GameState> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();

  const { data, error } = await supabase.rpc('set_cart_price', {
    p_cart_id: cartId,
    p_price: pricePerDog,
  });
  if (error) throw new Error(`set_cart_price: ${error.message}`);
  return parseGameState(data);
}

/**
 * Buy a commodity. `toCart` sends the goods straight onto a cart so it can keep serving;
 * omitting it stocks the player's own stores. Cash always comes from the player, and the
 * server checks that the destination cart is theirs.
 */
export async function buyItem(
  itemId: string,
  qty: number,
  toCart?: string,
): Promise<GameState> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();

  const { data, error } = await supabase.rpc('buy_item', {
    p_item: itemId,
    p_qty: qty,
    ...(toCart === undefined ? {} : { p_to_cart: toCart }),
  });
  if (error) throw new Error(error.message);
  return parseGameState(data);
}

export async function sellItem(
  itemId: string,
  qty: number,
  fromCart?: string,
): Promise<GameState> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();

  const { data, error } = await supabase.rpc('sell_item', {
    p_item: itemId,
    p_qty: qty,
    ...(fromCart === undefined ? {} : { p_from_cart: fromCart }),
  });
  if (error) throw new Error(error.message);
  return parseGameState(data);
}

/*
 * The static catalog, fetched once and memoised. It is reference data that cannot change at
 * runtime, so shipping it on every four-second poll would be pure waste — the reason the
 * server serves it from its own function rather than inside the state blob.
 */
let catalogCache: Promise<readonly CatalogItem[]> | null = null;

export function fetchItemsCatalog(): Promise<readonly CatalogItem[]> {
  catalogCache ??= (async () => {
    const supabase = getSupabase();
    if (!supabase) throw new NotConfiguredError();
    const { data, error } = await supabase.rpc('items_catalog_all');
    if (error) throw new Error(`items_catalog_all: ${error.message}`);
    if (!Array.isArray(data)) return [];
    return data.map((raw): CatalogItem => {
      const c = asRecord(raw, 'catalog item');
      return {
        id: str(c['id'], 'item.id'),
        name: str(c['name'], 'item.name'),
        category: str(c['category'], 'item.category'),
        stackMax: num(c['stackMax'], 'item.stackMax'),
        slot: typeof c['slot'] === 'string' ? c['slot'] : null,
        bonuses: (c['bonuses'] ?? {}) as Record<string, unknown>,
        price: money(c['price'], 'item.price'),
      };
    });
  })().catch((err: unknown) => {
    // Do not poison the cache: a failed boot fetch should be retried, not remembered.
    catalogCache = null;
    throw err;
  });
  return catalogCache;
}

/* ── Cart actions ─────────────────────────────────────────────────────────────────────
 * Each is an intent: the server validates ownership from the JWT, re-runs the tick, applies
 * the change and returns freshly computed state. None of them accept a result.
 */

async function rpc(fn: string, args: Record<string, unknown>): Promise<GameState> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return parseGameState(data);
}

export interface PlacementProbe {
  readonly ok: boolean;
  readonly lon?: number;
  readonly lat?: number;
  readonly wayClass?: string;
  readonly wayName?: string | null;
  readonly reason?: string;
}

/**
 * Ask whether a point can take a cart, and where exactly it would land. Used live while the
 * player moves the pointer, so the ghost shows the snapped position rather than the raw click.
 */
export async function checkPlacement(lon: number, lat: number): Promise<PlacementProbe> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('check_placement', { p_lon: lon, p_lat: lat });
  if (error) throw new Error(error.message);
  const r = asRecord(data, 'placement');
  return {
    ok: r['ok'] === true,
    ...(typeof r['lon'] === 'number' ? { lon: r['lon'] } : {}),
    ...(typeof r['lat'] === 'number' ? { lat: r['lat'] } : {}),
    ...(typeof r['wayClass'] === 'string' ? { wayClass: r['wayClass'] } : {}),
    ...(typeof r['wayName'] === 'string' ? { wayName: r['wayName'] } : {}),
    ...(typeof r['reason'] === 'string' ? { reason: r['reason'] } : {}),
  };
}

/**
 * Put the cart on the map. The cuisine is optional and normally omitted — the menu is chosen
 * afterwards, in the cart's own panel. Passing one places and opens for business in a single
 * call, which is what the end-to-end scripts do.
 */
export const placeCart = (cartId: string, lon: number, lat: number, cuisine?: string) =>
  rpc('place_cart', {
    p_cart_id: cartId,
    p_lon: lon,
    p_lat: lat,
    ...(cuisine === undefined ? {} : { p_cuisine: cuisine }),
  });

/** Choose what a placed cart sells. Names, prices and stocks it. Once only. */
export const setCartCuisine = (cartId: string, cuisine: string) =>
  rpc('set_cart_cuisine', { p_cart_id: cartId, p_cuisine: cuisine });

/**
 * Throw the run away and start over: one unplaced cart, the starting balance, nothing else.
 * Server-side it only touches rows belonging to the caller's own JWT.
 */
export const resetPlayer = () => rpc('reset_player', {});

export const relocateCart = (cartId: string, lon: number, lat: number) =>
  rpc('relocate_cart', { p_cart_id: cartId, p_lon: lon, p_lat: lat });

export const renameCart = (cartId: string, name: string) =>
  rpc('rename_cart', { p_cart_id: cartId, p_name: name });

/**
 * Cook what is on board. `quality` is the result of the prep interaction, in [0,1]; the server
 * clamps it and uses it only to reduce the yield, so it can cost the player food and never
 * create any. Ingredients for the whole attempt are spent either way.
 */
export const prepCart = (cartId: string, quality = 1, servings?: number) =>
  rpc('prep_cart', {
    p_cart_id: cartId,
    p_quality: quality,
    ...(servings === undefined ? {} : { p_servings: servings }),
  });

/** Wipe down. `quality` in [0,1] decides how clean it actually ends up. */
export const wipeCart = (cartId: string, quality = 1) =>
  rpc('wipe_cart', { p_cart_id: cartId, p_quality: quality });

/**
 * Buy exactly what N more servings need. Every line goes through the same market curve a manual
 * order would — this is a convenience, not a discount, and buying the parts by hand stays
 * marginally cheaper than buying kits.
 */
export const restockCart = (cartId: string, servings = 10) =>
  rpc('restock_cart', { p_cart_id: cartId, p_servings: servings });

/** What that restock would cost at current prices, so the button can say before it is pressed. */
export async function fetchRestockQuote(cartId: string, servings = 10): Promise<Money> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('restock_quote', {
    p_cart_id: cartId,
    p_servings: servings,
  });
  if (error) throw new Error(error.message);
  return money(asRecord(data, 'restock quote')['cost'], 'restockQuote.cost');
}

export const buyUpgrade = (cartId: string, upgradeId: string) =>
  rpc('buy_upgrade', { p_cart_id: cartId, p_upgrade: upgradeId });

export interface UpgradeOption {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly channel: 'design' | 'menu' | 'tuneup';
  readonly appeal: number;
  readonly level: number;
  readonly maxLevel: number;
  readonly nextCost: Money;
}

export async function fetchUpgradeOptions(cartId: string): Promise<readonly UpgradeOption[]> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('cart_upgrade_options', { p_cart_id: cartId });
  if (error) throw new Error(error.message);
  if (!Array.isArray(data)) return [];
  return data.map((raw): UpgradeOption => {
    const u = asRecord(raw, 'upgrade');
    return {
      id: str(u['id'], 'upgrade.id'),
      name: str(u['name'], 'upgrade.name'),
      description: str(u['description'], 'upgrade.description'),
      channel: str(u['channel'], 'upgrade.channel') as UpgradeOption['channel'],
      appeal: num(u['appeal'], 'upgrade.appeal'),
      level: num(u['level'], 'upgrade.level'),
      maxLevel: num(u['maxLevel'], 'upgrade.maxLevel'),
      nextCost: money(u['nextCost'], 'upgrade.nextCost'),
    };
  });
}

/* ── Land ─────────────────────────────────────────────────────────────────────────────
 * The client never prices land, decides who owns it, or works out what a build costs. It asks
 * for a parcel, renders what comes back, and sends an intent (CLAUDE.md non-negotiable #2).
 */

async function parcelRpc(fn: string, args: Record<string, unknown>): Promise<Parcel | null> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return parseParcel(data);
}

/** The parcel for a picked building's OSM way id. */
export const fetchParcelByOsmId = (osmWayId: number) =>
  parcelRpc('parcel_by_osm_id', { p_osm_way_id: osmWayId });

/** The parcel under a point — the fallback when a pick has coordinates but no feature id. */
export const fetchParcelAt = (lon: number, lat: number) =>
  parcelRpc('parcel_at', { p_lon: lon, p_lat: lat });

/** Re-read one parcel, after buying or building on it. */
export const fetchParcel = (parcelId: string) => parcelRpc('parcel_detail', { p_parcel_id: parcelId });

/**
 * Development only: fold the simulation forward. Pays the elapsed income *and consumes* that
 * much of the player's future, because `advance_player` moves `last_tick_at` with it — so this
 * is a clock, not a faucet, and the economy's no-free-money invariant survives it. Goes when
 * `reset_player` goes.
 */
export const devAdvanceMinutes = (minutes: number) =>
  rpc('dev_advance_minutes', { p_minutes: minutes });

/**
 * Development only: mint cash so anything with a price can be tested without grinding for it.
 * A faucet — the one thing the property suite proves no *player* action can do — and it goes
 * when accounts land, alongside `reset_player` and `dev_advance_minutes`.
 */
export const devGrantCash = (amount = 1000000) => rpc('dev_grant_cash', { p_amount: amount });

/* ── World surveys — the rest of Australia, on demand ─────────────────────────────── */

export type SurveyStatus = 'queued' | 'fetching' | 'done' | 'empty' | 'failed';

export interface SurveyCell {
  readonly x: number;
  readonly y: number;
  readonly status: SurveyStatus;
  readonly finishedAt: string | null;
}

export interface SurveyRequestResult {
  readonly queued: number;
  readonly known: number;
  readonly done: number;
  readonly deferred: number;
  readonly outside: number;
  readonly refused: string | null;
}

/**
 * Ask the server to survey z14 cells the player is looking at. The server validates every
 * cell (inside Australia, not already known), marks cells that already hold data as done on
 * the spot, and queues the rest for the cron worker — the client never talks to Overpass.
 */
export async function surveyRequest(
  cells: ReadonlyArray<readonly [number, number]>,
  lon: number,
  lat: number,
): Promise<SurveyRequestResult> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('survey_request', {
    p_cells: cells.map((c) => [c[0], c[1]]),
    p_lon: lon,
    p_lat: lat,
  });
  if (error) throw new Error(error.message);
  const r = asRecord(data, 'survey_request');
  return {
    queued: typeof r['queued'] === 'number' ? r['queued'] : 0,
    known: typeof r['known'] === 'number' ? r['known'] : 0,
    done: typeof r['done'] === 'number' ? r['done'] : 0,
    deferred: typeof r['deferred'] === 'number' ? r['deferred'] : 0,
    outside: typeof r['outside'] === 'number' ? r['outside'] : 0,
    refused: typeof r['refused'] === 'string' ? r['refused'] : null,
  };
}

/** Survey status for every known cell in a lon/lat bbox. Unknown cells simply have no row. */
export async function surveysInView(
  minLon: number,
  minLat: number,
  maxLon: number,
  maxLat: number,
): Promise<readonly SurveyCell[]> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('surveys_in_view', {
    min_lon: minLon,
    min_lat: minLat,
    max_lon: maxLon,
    max_lat: maxLat,
  });
  if (error) throw new Error(error.message);
  if (!Array.isArray(data)) return [];
  return data.map((raw) => {
    const c = asRecord(raw, 'survey_cell');
    const status = str(c['status'], 'survey_cell.status');
    return {
      x: num(c['cell_x'], 'survey_cell.cell_x'),
      y: num(c['cell_y'], 'survey_cell.cell_y'),
      status: (['queued', 'fetching', 'done', 'empty', 'failed'] as const).includes(
        status as SurveyStatus,
      )
        ? (status as SurveyStatus)
        : 'queued',
      finishedAt: typeof c['finished_at'] === 'string' ? c['finished_at'] : null,
    };
  });
}

/** Every surveyed cell's centre, for the globe's gold dots. */
export async function surveyMap(): Promise<ReadonlyArray<readonly [number, number]>> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('survey_map');
  if (error) throw new Error(error.message);
  if (!Array.isArray(data)) return [];
  return data.flatMap((raw) => {
    const c = asRecord(raw, 'survey_map');
    const lon = c['lon'];
    const lat = c['lat'];
    return typeof lon === 'number' && typeof lat === 'number' ? [[lon, lat] as const] : [];
  });
}

/** Unowned buildings near a point, cheapest first. One spatial query, not a spiral of probes. */
export async function fetchParcelsForSale(
  lon: number,
  lat: number,
  maxPrice?: number,
  radiusM = 1500,
  limit = 12,
): Promise<readonly Parcel[]> {
  const supabase = getSupabase();
  if (!supabase) throw new NotConfiguredError();
  const { data, error } = await supabase.rpc('parcels_for_sale_near', {
    p_lon: lon,
    p_lat: lat,
    p_radius_m: radiusM,
    p_limit: limit,
    ...(maxPrice === undefined ? {} : { p_max_price: maxPrice }),
  });
  if (error) throw new Error(error.message);
  if (!Array.isArray(data)) return [];
  // A listing row is a summary, not a full parcel: no ring, no ownership, no build options. The
  // inspector re-reads the parcel it actually opens.
  return data.flatMap((raw) => {
    const p = asRecord(raw, 'listing');
    return [
      {
        id: str(p['id'], 'listing.id'),
        osmWayId: typeof p['osmWayId'] === 'number' ? p['osmWayId'] : null,
        landUse: typeof p['landUse'] === 'string' ? p['landUse'] : null,
        areaM2: num(p['areaM2'], 'listing.areaM2'),
        levels: num(p['levels'], 'listing.levels'),
        roadClass: typeof p['roadClass'] === 'string' ? p['roadClass'] : null,
        price: money(p['price'], 'listing.price'),
        lon: num(p['lon'], 'listing.lon'),
        lat: num(p['lat'], 'listing.lat'),
        ring: [],
        frontageDeg: null,
        floors: typeof p['floors'] === 'number' ? p['floors'] : 1,
        owned: false,
        ownedByMe: false,
        pricePaid: null,
        resaleValue: null,
        reachableFloors: 1,
        floorSlots: 4,
        stairsCost: money(0, 'listing.stairsCost'),
        fixtures: [],
        storage: null,
        deliveries: [],
        shop: null,
      } satisfies Parcel,
    ];
  });
}

export const buyParcel = (parcelId: string) => rpc('buy_parcel', { p_parcel_id: parcelId });
export const sellParcel = (parcelId: string) => rpc('sell_parcel', { p_parcel_id: parcelId });

/*
 * The fit-out family. A building starts empty; these are how it becomes a shop. Every one is a
 * server intent — the market trade, the reachability rule, the storage capacity and the price
 * clamp all live in the SQL, and the client just renders whatever comes back.
 */
export const buyFixture = (parcelId: string, kind: string, floor: number) =>
  rpc('buy_fixture', { p_parcel_id: parcelId, p_kind: kind, p_floor: floor });
export const removeFixture = (fixtureId: string) =>
  rpc('remove_fixture', { p_fixture_id: fixtureId });
export const stockShelf = (fixtureId: string, itemId: string, price: number) =>
  rpc('stock_shelf', { p_fixture_id: fixtureId, p_item: itemId, p_price: price });
export const clearShelf = (fixtureId: string) => rpc('clear_shelf', { p_fixture_id: fixtureId });
export const orderStock = (parcelId: string, itemId: string, qty: number) =>
  rpc('order_stock', { p_parcel_id: parcelId, p_item: itemId, p_qty: qty });
